/**
 * @name LoginOperador
 * @description Componente de login para operadores liberarem o totem
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/components/pages/LoginOperador.tsx
 */

import logoDark from "@/assets/png/logo-dark.png";
import GradientBackground from "@/components/common/backgrounds/GradientBackground";
import { StyledTextField } from "@/components/common/fields";
import { useOperadorContext } from "@/contexts/OperadorContext";
import { useFullscreen } from "@/hooks";
import useThemeContext from "@/themes/hooks/useThemeContext";
import { getGlassButton, getGlassCard } from "@/themes/styles/glassStyles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

const LoginOperador: React.FC = () => {
  const { theme } = useThemeContext();
  const { autenticarOperador } = useOperadorContext();
  const {
    enterFullscreen,
    lockFullscreen,
    isSupported: isFullscreenSupported,
  } = useFullscreen();

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [isCarregando, setIsCarregando] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !senha.trim()) return;

    try {
      setIsCarregando(true);
      const sucesso = await autenticarOperador(email.trim(), senha);

      // Se o login foi bem-sucedido, entrar em tela cheia e travar
      if (sucesso && isFullscreenSupported) {
        console.log("🖥️ Login bem-sucedido, entrando em tela cheia travada...");
        const entrou = await enterFullscreen();
        if (entrou) {
          lockFullscreen(); // Trava a tela cheia após entrar
        }
      }
    } catch (error) {
      console.error("Erro no login:", error);
    } finally {
      setIsCarregando(false);
      setSenha(""); // Limpar senha por segurança
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit(e as any);
    }
  };

  return (
    <GradientBackground>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: "550px",
            width: "100%",
            px: 3,
          }}
        >
          <Card
            sx={{
              p: 5,
              ...getGlassCard(theme, "strong"),
              borderRadius: 4,
            }}
          >
            <CardContent>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                {/* Logo da Empresa */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 3,
                    transition: "transform 0.3s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <img
                    src={logoDark}
                    alt="IS Integrado Soluções"
                    style={{
                      height: 120,
                      width: "auto",
                      maxWidth: "280px",
                      objectFit: "contain",
                      filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
                    }}
                  />
                </Box>

                {/* Mensagem de Boas-vindas */}
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  <span>🍽️ </span>Bem-vindos à Cantina Digital
                </Typography>

                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                  🔐 Acesso do Operador
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontStyle: "italic",
                    opacity: 0.8,
                  }}
                >
                  Faça login para liberar o totem e permitir que os alunos
                  realizem seus pedidos
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                {/* Campo Email */}
                <StyledTextField
                  fullWidth
                  label="Email do Operador"
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                  onKeyPress={handleKeyPress}
                  disabled={isCarregando}
                  required
                  height={56}
                  fontSize="1.1rem"
                  marginBottom={3}
                  withGlassContainer={false}
                />
                {/* Campo Senha */}
                <StyledTextField
                  fullWidth
                  label="Senha"
                  type={mostrarSenha ? "text" : "password"}
                  value={senha}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSenha(e.target.value)
                  }
                  onKeyPress={handleKeyPress}
                  disabled={isCarregando}
                  required
                  height={56}
                  fontSize="1.1rem"
                  marginBottom={4}
                  withGlassContainer={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setMostrarSenha(!mostrarSenha)}
                          edge="end"
                          disabled={isCarregando}
                        >
                          {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {/* Botão de Login */}
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={!email.trim() || !senha.trim() || isCarregando}
                  sx={{
                    ...getGlassButton(theme, "primary"),
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    position: "relative",
                    textTransform: "none",
                    "&.Mui-disabled": {
                      backgroundColor: isCarregando
                        ? "primary.main"
                        : "action.disabledBackground",
                      color: isCarregando
                        ? "primary.contrastText"
                        : "action.disabled",
                      opacity: isCarregando ? 0.8 : 0.5,
                    },
                  }}
                >
                  {isCarregando ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography>Autenticando</Typography>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 4,
                            height: 4,
                            bgcolor: "common.white",
                            borderRadius: "50%",
                            animation: "pulse 1s infinite",
                            "@keyframes pulse": {
                              "0%": { opacity: 0.2 },
                              "20%": { opacity: 1 },
                              "100%": { opacity: 0.2 },
                            },
                            animationDelay: "0s",
                          }}
                        />
                        <Box
                          sx={{
                            width: 4,
                            height: 4,
                            bgcolor: "common.white",
                            borderRadius: "50%",
                            animation: "pulse 1s infinite",
                            animationDelay: "0.2s",
                          }}
                        />
                        <Box
                          sx={{
                            width: 4,
                            height: 4,
                            bgcolor: "common.white",
                            borderRadius: "50%",
                            animation: "pulse 1s infinite",
                            animationDelay: "0.4s",
                          }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    "Liberar Totem"
                  )}
                </Button>
                {/* Rodapé com informações da empresa */}
                <Box
                  sx={{
                    mt: 4,
                    pt: 3,
                    textAlign: "center",
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Desenvolvido por
                  </Typography>
                  <Typography
                    variant="body2"
                    color="primary.main"
                    fontWeight="600"
                  >
                    IS - Integrado Soluções
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </GradientBackground>
  );
};

export default LoginOperador;
